import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const DepositHistory = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const UserId = localStorage.getItem("UserId");

  // Status Badge Component - Fixed to properly handle txstatus
  const StatusBadge = ({ status }) => {
    // Convert to string for consistent comparison
    const statusStr = String(status).toUpperCase();
    
    let label = "Unknown";
    let color = "#6b7280";

    if (statusStr === "PENDING") {
      label = "Pending";
      color = "#f59e0b";
    } else if (statusStr === "COMPLETED") {
      label = "Completed";
      color = "#10b981";
    } else if (statusStr === "FAILED") {
      label = "Failed";
      color = "#ef4444";
    }

    console.log("Status received:", status, "Converted to:", statusStr, "Label:", label);

    return (
      <span className="status-badge" style={{ backgroundColor: color }}>
        {label}
      </span>
    );
  };

  // Fetch deposit history
  const fetchDepositHistory = async () => {
    if (!UserId) {
      setDeposits([]);
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const res = await axios.post(`${API_BASE}/deposithistory`, { 
        UserId: UserId, 
        Currency: "USDT" 
      });
      
      console.log("Full API Response:", JSON.stringify(res.data, null, 2));
      
      if (res.data && Array.isArray(res.data)) {
        const formattedData = res.data.map((record, index) => {
          // Debug log each record
          console.log(`Record ${index}:`, {
            txstatus: record.txstatus,
            typeOfTxstatus: typeof record.txstatus,
            amount: record.amount,
            netamount: record.netamount,
            txhash: record.txhash,
            created_at: record.created_at
          });
          
          return {
            id: `DP${String(index + 1).padStart(4, '0')}`,
            amount: parseFloat(record.netamount) || parseFloat(record.amount) || 0,
            method: 'USDT (BEP20)',
            from: record.from_address || 'Wallet',
            date: record.created_at ? new Date(record.created_at).toLocaleString() : '-',
            status: String(record.txstatus), // Force to string
            txHash: record.txhash || '-',
            tokenAmount: record.tokenAmount || '-',
            remarks: record.remarks || '-'
          };
        });
        
        console.log("Formatted Deposits:", formattedData);
        setDeposits(formattedData);
      } else {
        setDeposits([]);
      }
    } catch (err) {
      console.error("Deposit History Error:", err);
      setError("Failed to fetch deposit history");
      setDeposits([]);
      toast.error("Failed to load deposit history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepositHistory();
  }, []);

  // Filter deposits
  const filteredDeposits = deposits.filter(deposit => {
    let matchesFilter = true;
    if (filter === 'completed') {
      matchesFilter = deposit.status === "1";
    } else if (filter === 'pending') {
      matchesFilter = deposit.status === "0";
    } else if (filter === 'failed') {
      matchesFilter = deposit.status === "2";
    }
    
    const matchesSearch = deposit.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          deposit.method.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (deposit.txHash && deposit.txHash.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  // Pagination
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const paginatedDeposits = filteredDeposits.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredDeposits.length / entriesPerPage);
  const totalDeposited = deposits.filter(d => d.status === "1").reduce((sum, d) => sum + d.amount, 0);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['ID', 'Amount (USD)', 'Method', 'Date', 'Status', 'Transaction Hash', 'Token Amount'];
    const csvData = filteredDeposits.map(deposit => [
      deposit.id,
      `$${deposit.amount.toFixed(2)}`,
      deposit.method,
      deposit.date,
      deposit.status === "1" ? 'Completed' : (deposit.status === "0" ? 'Pending' : 'Failed'),
      deposit.txHash,
      deposit.tokenAmount
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deposit_history_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported successfully!");
  };

  // Debug: Log current deposits and their statuses
  useEffect(() => {
    console.log("Current deposits state:", deposits);
    deposits.forEach(d => {
      console.log(`Deposit ${d.id}: status = ${d.status}, type = ${typeof d.status}`);
    });
  }, [deposits]);

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="deposit-history-page">
        <div className="page-header">
          <div>
            <h1>Deposit History</h1>
            <p>Track all your deposit transactions</p>
          </div>
          <div className="total-deposited">
            <span>Total Deposited</span>
            <strong>${totalDeposited.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
          </div>
        </div>

        {/* Filters */}
        <div className="table-filters">
          <div className="search-input-wrapper">
            <i className="fa fa-search"></i>
            <input
              type="text"
              placeholder="Search by ID or TX Hash..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="filter-group">
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <select 
              value={entriesPerPage} 
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10 entries</option>
              <option value={25}>25 entries</option>
              <option value={50}>50 entries</option>
            </select>
            <button className="export-btn" onClick={exportToCSV}>
              <i className="fa fa-download"></i> Export
            </button>
          </div>
        </div>

        {/* Premium Table Container */}
        <div className="premium-table-container">
          <table className="premium-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Amount (USD)</th>
                <th>Token Amount</th>
                <th>Date</th>
                <th>Transaction Hash</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                    <i className="fa fa-spinner fa-spin"></i> Loading deposit history...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px", color: "var(--danger)" }}>
                    <i className="fa fa-exclamation-circle"></i> {error}
                  </td>
                </tr>
              ) : paginatedDeposits.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                    <i className="fa fa-inbox"></i> No deposit records found
                  </td>
                </tr>
              ) : (
                paginatedDeposits.map((deposit, idx) => (
                  <tr key={deposit.id}>
                    <td data-label="#">{indexOfFirst + idx + 1}</td>
                    <td data-label="Amount" className="amount positive">
                      ${deposit.amount.toFixed(2)}
                    </td>
                    <td data-label="Token Amount">
                      {deposit.tokenAmount !== '-' ? parseFloat(deposit.tokenAmount).toFixed(4) + ' USDT' : '-'}
                    </td>
                    <td data-label="Date">{deposit.date}</td>
                    <td data-label="Transaction Hash">
                      {deposit.txHash !== '-' ? (
                        <>
                          <code className="tx-hash">{deposit.txHash.slice(0, 16)}...</code>
                          <button 
                            className="copy-hash-btn" 
                            onClick={() => navigator.clipboard.writeText(deposit.txHash)}
                            title="Copy full hash"
                          >
                            <i className="fa fa-copy"></i>
                          </button>
                          <a
                            href={`https://bscscan.com/tx/${deposit.txHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="view-tx-link"
                            title="View on BscScan"
                          >
                            <i className="fa fa-external-link"></i>
                          </a>
                        </>
                      ) : (
                        <span className="pending-text">-</span>
                      )}
                    </td>
                    <td data-label="Status">
                      <StatusBadge status={deposit.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && !loading && !error && filteredDeposits.length > 0 && (
            <div className="premium-table-footer">
              <div className="table-info">
                Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredDeposits.length)} of {filteredDeposits.length} entries
              </div>
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <i className="fa fa-chevron-left"></i>
                </button>
                {[...Array(Math.min(totalPages, 10))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 10) {
                    pageNum = i + 1;
                  } else if (currentPage <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 4) {
                    pageNum = totalPages - 9 + i;
                  } else {
                    pageNum = currentPage - 5 + i;
                  }
                  if (pageNum >= 1 && pageNum <= totalPages) {
                    return (
                      <button
                        key={pageNum}
                        className={currentPage === pageNum ? 'active-page' : ''}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return null;
                })}
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DepositHistory;